import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";

export default function Navbar() {
  const { lang, toggle, t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const isLanding = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const sections = [
    { hash: "pillars", label: t.nav.pillars },
    { hash: "method", label: t.nav.method },
    { hash: "coach", label: t.nav.coach },
    { hash: "services", label: t.nav.services },
  ];

  const handleSection = (hash) => {
    setOpen(false);
    if (isLanding) {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${hash}`);
    }
  };

  const handleHome = () => {
    setOpen(false);
    if (isLanding) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const linkCls = "text-xs font-mono-mtk uppercase tracking-[0.25em] text-zinc-400 hover:text-mtk-gold transition-colors cursor-pointer";
  const mobileLinkCls = "font-mono-mtk text-xs uppercase tracking-[0.25em] text-zinc-300 cursor-pointer text-left";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-mtk-bg/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button onClick={handleHome} className="flex items-center gap-2 group" data-testid="brand-logo">
          <span className="font-heading text-lg md:text-xl font-black tracking-[0.18em] text-white">
            MTK
          </span>
          <span className="hidden sm:inline-block w-px h-4 bg-mtk-gold/50" />
          <span className="hidden sm:inline-block font-mono-mtk text-[10px] tracking-[0.3em] text-mtk-gold uppercase">
            SYSTEM
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {sections.map((s) => (
            <button
              key={s.hash}
              onClick={() => handleSection(s.hash)}
              className={linkCls}
              data-testid={`nav-link-${s.hash}`}
            >
              {s.label}
            </button>
          ))}
          <Link
            to="/blog"
            className={linkCls}
            data-testid="nav-link-blog"
          >
            {t.blog.title}
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="language-toggle"
            className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-mtk-gold transition-colors border border-white/10 hover:border-mtk-gold/40 px-3 py-2"
          >
            <span className={lang === "es" ? "text-mtk-gold" : ""}>ES</span>
            <span className="mx-1.5 text-white/20">/</span>
            <span className={lang === "en" ? "text-mtk-gold" : ""}>EN</span>
          </button>

          <button onClick={() => handleSection("apply")} className="hidden md:inline-block">
            <Button
              data-testid="navbar-apply-btn"
              className="rounded-none bg-mtk-gold text-mtk-bg hover:bg-mtk-gold-light font-bold uppercase tracking-[0.2em] text-xs h-10 px-5"
            >
              {t.nav.apply}
            </Button>
          </button>

          <Link to="/admin/login" className="hidden lg:inline-block" data-testid="admin-link">
            <span className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-mtk-gold transition-colors">
              {t.nav.admin}
            </span>
          </Link>

          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            <div className="w-5 flex flex-col gap-1">
              <span className="h-px bg-white" />
              <span className="h-px bg-white" />
              <span className="h-px bg-white" />
            </div>
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/5 bg-mtk-bg/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {sections.map((s) => (
              <button
                key={s.hash}
                onClick={() => handleSection(s.hash)}
                className={mobileLinkCls}
              >
                {s.label}
              </button>
            ))}
            <Link to="/blog" onClick={() => setOpen(false)} className={mobileLinkCls}>
              {t.blog.title}
            </Link>
            <button onClick={() => handleSection("apply")}>
              <Button className="w-full rounded-none bg-mtk-gold text-mtk-bg hover:bg-mtk-gold-light font-bold uppercase tracking-[0.2em] text-xs h-11">
                {t.nav.apply}
              </Button>
            </button>
            <Link to="/admin/login" onClick={() => setOpen(false)} className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              {t.nav.admin} →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
